import React, { useState, useEffect } from "react";
import {
  handleLogin,
  handleRegister,
  requestPasswordResetOtp,
  resetPasswordWithOtp,
} from "../../service/AuthService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

export default function AuthPage() {
  const [authMode, setAuthMode] = useState("login");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    role: "student",
  });

  // Auto-redirect if already logged in
  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr && userStr !== "null" && userStr !== "undefined") {
      try {
        const user = JSON.parse(userStr);
        if (user?.email && user?.role) {
          // If already on the correct path, don't re-navigate
          const currentPath = window.location.pathname;
          let targetPath = "/dashboard";
          
          if (user.role === 'superadmin') {
            targetPath = "/superadmin";
          } else if (user.tenantEmail) {
            targetPath = `/${user.tenantEmail}/lms`;
          }

          if (currentPath !== targetPath && currentPath === "/") {
            navigate(targetPath, { replace: true });
          }
        }
      } catch (e) {
        console.error("Invalid session found, clearing storage...", e);
        localStorage.removeItem("user");
      }
    }
  }, [navigate]);

  const [forgotForm, setForgotForm] = useState({
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });

  const isLogin = authMode === "login";
  const isForgotPassword = authMode === "forgot";
  const passwordsMatch = isLogin || form.password === form.confirmPassword;
  const forgotPasswordsMatch =
    forgotForm.confirmPassword === "" || forgotForm.newPassword === forgotForm.confirmPassword;

  const isFormComplete = isLogin
    ? Boolean(form.email.trim() && form.password)
    : Boolean(
        form.email.trim() &&
          form.name.trim() &&
          form.password &&
          form.confirmPassword
      );

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleForgotChange = (e) => {
    setForgotForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const goToLogin = () => {
    setAuthMode("login");
    setForgotStep(1);
    setForgotForm({
      email: "",
      otp: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  const openForgotPassword = () => {
    setAuthMode("forgot");
    setForgotStep(1);
    setForgotForm({
      email: form.email,
      otp: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!isFormComplete) return;
    if (!isLogin && form.password !== form.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      setIsSubmitting(true);

      if (isLogin) {
        const res = await handleLogin(form.email, form.password);

        if (res.status === "success") {
          localStorage.setItem("user", JSON.stringify(res));
          toast.success("Login successful");
          
          if (res.tenantEmail && res.role !== 'superadmin') {
            navigate(`/${res.tenantEmail}/lms`);
          } else if (res.role === 'superadmin') {
            navigate("/superadmin");
          } else {
            navigate("/dashboard");
          }
        } else {
          const message = res?.message || "Invalid credentials";
          toast.error(message);
        }
      } else {
        const res = await handleRegister(form);
        if (res?.status === "success") {
          toast.success("Account created. You can now log in.");
          setAuthMode("login");
        } else {
          toast.error(res?.message || "Could not register");
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!forgotForm.email.trim()) {
      toast.error("Please enter your registered email");
      return;
    }

    try {
      setIsSubmitting(true);
      const res = await requestPasswordResetOtp(forgotForm.email);

      if (res?.status === "success") {
        toast.success(res.message || "OTP sent");
        setForgotStep(2);
      } else {
        toast.error(res?.message || "Could not send OTP");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!forgotForm.email.trim() || !forgotForm.otp.trim() || !forgotForm.newPassword) {
      toast.error("Please complete all fields");
      return;
    }

    if (forgotForm.newPassword !== forgotForm.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      setIsSubmitting(true);
      const res = await resetPasswordWithOtp({
        email: forgotForm.email,
        otp: forgotForm.otp,
        newPassword: forgotForm.newPassword,
      });

      if (res?.status === "success") {
        toast.success(res.message || "Password reset successful");
        setForm((prev) => ({
          ...prev,
          email: forgotForm.email,
          password: "",
          confirmPassword: "",
        }));
        goToLogin();
      } else {
        toast.error(res?.message || "Could not reset password");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4 relative">


      <div className="max-w-5xl w-full grid grid-cols-1 lg:grid-cols-[1.3fr,1fr] gap-10 items-center">
        <div className="hidden lg:block">
          <div className="relative min-h-[620px] overflow-hidden rounded-[2rem] border border-slate-800 bg-slate-900 shadow-[0_24px_80px_rgba(15,23,42,0.55)]">
            <div
              className="absolute inset-0 bg-cover bg-center scale-105"
              style={{ backgroundImage: "url('/hero-bg.jpg')" }}
            />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.18),rgba(2,6,23,0.78)_45%,rgba(2,6,23,0.96))]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,0.24),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(16,185,129,0.18),transparent_30%)]" />

            <div className="relative flex h-full min-h-[620px] flex-col justify-end gap-4 p-8 xl:p-10">
              <div className="inline-flex w-fit rounded-full border border-white/10 bg-slate-950/45 px-3 py-1.5 backdrop-blur">
                <p className="text-xs font-semibold tracking-[0.25em] text-slate-200 uppercase">
                  PENSENSE
                </p>
              </div>

              <h1 className="max-w-lg text-4xl font-semibold tracking-tight text-white xl:text-[2.75rem]">
                Sign in to your study analytics.
              </h1>
              <p className="max-w-md text-sm leading-6 text-slate-200/88">
                Track your subjects, understand where your time goes, and get AI
                support on the topics that matter most.
              </p>

              <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-200">
                <span className="rounded-full border border-white/10 bg-slate-950/45 px-3 py-1 backdrop-blur">
                  Real-time dashboard
                </span>
                <span className="rounded-full border border-white/10 bg-slate-950/45 px-3 py-1 backdrop-blur">
                  AI-powered study help
                </span>
                <span className="rounded-full border border-white/10 bg-slate-950/45 px-3 py-1 backdrop-blur">
                  Subject insights
                </span>
              </div>

              <button
                type="button"
                onClick={() => navigate("/activation-steps")}
                className="mt-4 inline-flex w-fit items-center justify-center rounded-xl border border-sky-400/35 bg-sky-500/15 px-4 py-2.5 text-sm font-medium text-sky-100 backdrop-blur transition hover:bg-sky-500/25 hover:border-sky-300/55"
              >
                Activation Steps
              </button>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-0.5 bg-gradient-to-tr from-indigo-500 via-sky-500 to-emerald-400 opacity-40 blur-2xl" />
          <div className="relative rounded-3xl border border-slate-800 bg-slate-950/90 p-7 shadow-[0_22px_65px_rgba(15,23,42,0.9)] backdrop-blur">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs font-semibold tracking-[0.22em] text-slate-400 uppercase">
                  {isForgotPassword ? "Recover account" : isLogin ? "Welcome back" : "Create account"}
                </p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight">
                  {isForgotPassword ? "Forgot password" : isLogin ? "Sign in" : "Register"}
                </h2>
                {!isLogin && !isForgotPassword && (
                  <p className="mt-1 text-xs font-medium text-sky-400">
                    Registering as a Student
                  </p>
                )}
              </div>
              <span className="inline-flex min-h-9 items-center justify-center rounded-2xl border border-slate-800 bg-slate-900 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-300">
                PENSENSE
              </span>
            </div>

            {!isForgotPassword ? (
              <form onSubmit={handleSubmit} className="space-y-4">


                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300">
                    Email
                  </label>
                  <input
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-indigo-500/60 focus:border-indigo-500/60"
                  />
                </div>

                {!isLogin && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-300">
                      Name
                    </label>
                    <input
                      name="name"
                      placeholder="Your name"
                      value={form.name}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-indigo-500/60 focus:border-indigo-500/60"
                    />
                  </div>
                )}

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-slate-300">
                      Password
                    </label>
                    {isLogin && (
                      <button
                        type="button"
                        onClick={openForgotPassword}
                        className="text-xs font-medium text-sky-300 transition hover:text-sky-200"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter your password"
                      value={form.password}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-indigo-500/60 focus:border-indigo-500/60 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                    >
                      {showPassword ? (
                         <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      ) : (
                         <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      )}
                    </button>
                  </div>
                </div>

                {!isLogin && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-300">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="confirmPassword"
                        placeholder="Re-enter your password"
                        value={form.confirmPassword}
                        onChange={handleChange}
                        className={`w-full rounded-xl border bg-slate-950/60 px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:ring-2 focus:border-indigo-500/60 pr-10 ${
                          form.confirmPassword && !passwordsMatch
                            ? "border-rose-500/70 focus:ring-rose-500/40"
                            : "border-slate-800 focus:ring-indigo-500/60"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                      >
                        {showPassword ? (
                           <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        ) : (
                           <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        )}
                      </button>
                    </div>
                    {form.confirmPassword && !passwordsMatch && (
                      <p className="text-xs text-rose-400">
                        Passwords must match to create your account.
                      </p>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || !isFormComplete || !passwordsMatch}
                  className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-400 px-4 py-2.5 text-sm font-medium text-slate-950 shadow-lg shadow-indigo-900/40 hover:brightness-105 disabled:opacity-60 disabled:cursor-not-allowed transition"
                >
                  {isSubmitting ? "Please wait..." : isLogin ? "Sign in" : "Create account"}
                </button>
              </form>
            ) : (
              <div className="space-y-4">
                <p className="text-sm leading-6 text-slate-400">
                  Enter your registered email and we&apos;ll send an OTP. After you verify it, you can set a new password.
                </p>

                <form
                  onSubmit={forgotStep === 1 ? handleRequestOtp : handleResetPassword}
                  className="space-y-4"
                >
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-300">
                      Registered Email
                    </label>
                    <input
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={forgotForm.email}
                      onChange={handleForgotChange}
                      className="w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-indigo-500/60 focus:border-indigo-500/60"
                    />
                  </div>

                  {forgotStep === 2 && (
                    <>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-slate-300">
                          OTP
                        </label>
                        <input
                          name="otp"
                          placeholder="Enter 6-digit OTP"
                          value={forgotForm.otp}
                          onChange={handleForgotChange}
                          className="w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-indigo-500/60 focus:border-indigo-500/60"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-slate-300">
                          New Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            name="newPassword"
                            placeholder="Enter a new password"
                            value={forgotForm.newPassword}
                            onChange={handleForgotChange}
                            className="w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-indigo-500/60 focus:border-indigo-500/60 pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                          >
                            {showPassword ? (
                               <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                            ) : (
                               <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-slate-300">
                          Confirm New Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            name="confirmPassword"
                            placeholder="Re-enter new password"
                            value={forgotForm.confirmPassword}
                            onChange={handleForgotChange}
                            className={`w-full rounded-xl border bg-slate-950/60 px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:ring-2 focus:border-indigo-500/60 pr-10 ${
                              forgotForm.confirmPassword && !forgotPasswordsMatch
                                ? "border-rose-500/70 focus:ring-rose-500/40"
                                : "border-slate-800 focus:ring-indigo-500/60"
                            }`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                          >
                            {showPassword ? (
                               <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                            ) : (
                               <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                            )}
                          </button>
                        </div>
                        {forgotForm.confirmPassword && !forgotPasswordsMatch && (
                          <p className="text-xs text-rose-400">
                            Passwords must match to reset your password.
                          </p>
                        )}
                      </div>
                    </>
                  )}

                  <button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      (forgotStep === 1
                        ? !forgotForm.email.trim()
                        : !forgotForm.email.trim() ||
                          !forgotForm.otp.trim() ||
                          !forgotForm.newPassword ||
                          !forgotForm.confirmPassword ||
                          !forgotPasswordsMatch)
                    }
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-400 px-4 py-2.5 text-sm font-medium text-slate-950 shadow-lg shadow-indigo-900/40 hover:brightness-105 disabled:opacity-60 disabled:cursor-not-allowed transition"
                  >
                    {isSubmitting
                      ? "Please wait..."
                      : forgotStep === 1
                      ? "Send OTP"
                      : "Reset password"}
                  </button>

                  {forgotStep === 2 && (
                    <button
                      type="button"
                      onClick={handleRequestOtp}
                      disabled={isSubmitting || !forgotForm.email.trim()}
                      className="w-full text-center text-xs text-sky-300 transition hover:text-sky-200 disabled:opacity-50"
                    >
                      Resend OTP
                    </button>
                  )}
                </form>

                <button
                  type="button"
                  onClick={goToLogin}
                  className="w-full text-center text-xs text-slate-400 hover:text-slate-200 transition"
                >
                  Back to sign in
                </button>
              </div>
            )}

            {!isForgotPassword && (
              <button
                type="button"
                onClick={() => {
                  setAuthMode(isLogin ? "register" : "login");
                  setForm((prev) => ({
                    ...prev,
                    password: "",
                    confirmPassword: "",
                  }));
                }}
                className="mt-4 w-full text-center text-xs text-slate-400 hover:text-slate-200 transition"
              >
                {isLogin
                  ? "New here? Create an account"
                  : "Already have an account? Sign in"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/919819575898"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-[100] flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_4px_14px_rgba(37,211,102,0.4)] hover:bg-[#20bd5a] hover:scale-110 transition-all duration-300"
        aria-label="Chat on WhatsApp"
      >
        <svg
          width="30"
          height="30"
          viewBox="0 0 24 24"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.82 9.82 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
}
